import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule, MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, map } from 'rxjs';
import { AuthService } from '../../core/services/auth/auth.service';
import { Release } from '../../models/release';
import { Suite } from '../../models/suite';
import { TestCase } from '../../models/test-case';
import { TestPlan } from '../../models/test-plan';
import { TestCaseService } from '../../services/test-case/test-case.service';
import { ReleaseViewStateService } from '../../services/view-state/release-view-state.service';
import { AddSuiteDialogComponent } from '../add-suite-dialog/add-suite-dialog.component';

@Component({
  selector: 'app-release-detail',
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatListModule,
    MatSnackBarModule,
  ],
  templateUrl: './release-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './release-detail.component.scss',
})
export class ReleaseDetailComponent {
  private testCaseService = inject(TestCaseService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private viewState = inject(ReleaseViewStateService);
  private snack = inject(MatSnackBar);
  readonly authService = inject(AuthService);

  release = toSignal(this.route.data.pipe(map((d) => d['releaseData'] as Release | null)), {
    initialValue: null,
  });

  suites = signal<Suite[]>([]);
  testCases = signal<TestCase[]>([]);
  selectedTestPlan = signal<TestPlan | null>(null);
  isSuitesLoading = signal(false);
  isTestCasesLoading = signal(false);
  lastSelectedPlanId = signal<number | null>(null);
  isLoading = signal(false);
  selectedPlanModel: TestPlan | null = null;

  comparePlanById = (a?: TestPlan | null, b?: TestPlan | null) =>
    !!a && !!b && a.testPlanId === b.testPlanId;

  @ViewChild('testCaseList') testCaseList!: MatSelectionList;
  private restoredForReleaseId: number | null = null;

  constructor() {
    effect(() => {
      const rel = this.release();
      if (!rel) return;

      if (this.restoredForReleaseId === rel.releaseId) return;

      const remembered = this.viewState.getSelectedPlanId(rel.releaseId);
      this.lastSelectedPlanId.set(remembered);

      if (remembered != null) {
        const found = rel.suites
          .flatMap((s) => s.testPlanSummaries)
          .find((tp) => tp.testPlanId === remembered);

        if (found) {
          // do the writes; they won't retrigger this effect unless you *read* them here
          this.selectedTestPlan.set(found);
          // queue to avoid any synchronous template churn
          queueMicrotask(() => this.loadTestCasesForTestPlan(found.testPlanId));
        }
      }

      // mark as restored so this effect doesn't run again for this release
      this.restoredForReleaseId = rel.releaseId;

      const plan = this.selectedTestPlan();
      this.selectedPlanModel = plan ? plan : null;

      const currentFirst = this.selectedPlanModel ?? null;

      const currentId = currentFirst?.testPlanId ?? null;
      const nextId = plan?.testPlanId ?? null;

      if (currentId !== nextId) {
        this.selectedPlanModel = plan;
      }
    });
  }

  selectSuite(testPlan: TestPlan) {
    this.selectedTestPlan.set(testPlan);
    this.lastSelectedPlanId.set(testPlan.testPlanId);
    const relId = this.release()?.releaseId;
    if (relId) this.viewState.setSelectedPlanId(relId, testPlan.testPlanId);
    this.testCases.set([]);

    this.loadTestCasesForTestPlan(testPlan.testPlanId);
  }

  onTestCaseChange(e: MatSelectionListChange) {
    this.isLoading.set(true);
    const testCase = e.options[0]?.value;
    if (testCase) {
      const relId = this.release()?.releaseId ?? null;
      const planId = this.selectedTestPlan()?.testPlanId ?? null;
      if (relId != null) this.viewState.setSelectedPlanId(relId, planId);

      this.router.navigate(
        ['releases', this.release()?.releaseId, 'test-case', testCase.testCaseId],
        { queryParams: { planId } },
      );
    }
  }

  trackByTestPlanId = (_: number, x: TestPlan) => x.testPlanId;

  onAddNewSuiteClick() {
    if (this.release() != null) {
      // this.suiteService
      //   .getSuitesFromAzureByRelease(
      //     this.release()!.releaseAzureId,
      //     this.release()!.rootSuiteAzureId,
      //   )
      //   .subscribe({
      //     next: (suites) => {
      //       this.azureSuites.set(suites);
      //       console.log(suites);
      //     },
      //     error: (err) => console.error('Error loading release Azure suite details:', err),
      //   });
      const ref = this.dialog.open(AddSuiteDialogComponent, {
        disableClose: true, // force explicit Cancel / Save
        autoFocus: 'first-tabbable',
        panelClass: 'suite-dialog-panel',
        data: {
          releaseAzureId: this.release()!.releaseAzureId,
          rootSuiteAzureId: this.release()!.rootSuiteAzureId,
        },
      });

      ref.afterClosed().subscribe((saved: boolean) => {
        if (saved) {
          // this.loadReleases();
          console.log('got here');
        }
      });
    }
  }

  private loadTestCasesForTestPlan(testPlanId: number): void {
    this.isTestCasesLoading.set(true);
    this.testCaseService
      .getByTestPlanId(testPlanId)
      .pipe(finalize(() => this.isTestCasesLoading.set(false)))
      .subscribe({
        next: (testCases) => this.testCases.set(testCases),
        error: (err) => {
          console.error('Error loading test cases for test plan:', err);
          this.snack.open('Failed to load test cases. Please try again.', 'Dismiss', {
            duration: 5000,
          });
        },
      });
  }
}
